import {
    ChatType,
    IChat,
    IChatMessage,
    IChatReceipt,
    IRequestChatMessages
} from '@app-types/websocket/chat.types';
import {IFamilyMember} from '@app-types/websocket/family.types';
import {displayErrorMessage, displaySuccessMessage} from '@services/user/user.actions';
import {Checkbox, IconButton, ListItemIcon, Menu, MenuItem, withStyles, Dialog, DialogTitle,} from '@material-ui/core';
import * as ChatActions from '@services/chat/chat.actions';
import * as React from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import TranslateNew from '@translate/TranslateNew';
import {WithStyles} from '@material-ui/core/styles';
import {chatStyle} from './chat2.container.style';
import SearchIcon from "@material-ui/icons/SearchRounded";
import SettingsIcon from "@material-ui/icons/TuneRounded";
import BottomBar from '@components/calendar2/fab/BottomBar';
import ChatListItem from '@components/chat/ChatListItem';
import ChatTopBar from '@components/chat/ChatTopBar';
import ChatBottomBar from '@components/chat/ChatBottomBar';
import ChatRoom from '@components/chat/ChatRoom';
import ChatPane from '@components/chat/ChatPane';
import { secondaryColor, styleColors } from 'src/main.style';
import CloseIcon from '@material-ui/icons/CloseRounded';
import AddIcon from '@material-ui/icons/Add';
import BackSwipe from '@components/chat/BackSwipe';
import { ISuggestion } from '@app-types/websocket/suggestion.types';
import { DialogTop } from '@components/economy/DialogComponens';
import EditChat from '@components/chat/EditChat';
import * as CallActions from '@services/calls/calls.actions';
import { ICall, ICallParticipant } from '@app-types/websocket/calls.types';
import { buildChatPath, getChatTargetMessageId } from '@router/chatRoute';
import { FamilyMemberType } from '@services/family/family.constants';
import { chatListPreviewsHidden } from '@services/chat/chatPreview';
import { ensureCallPermission } from '@services/permissions/permission';
import CallPermissionDialog from '@components/calls/CallPermissionDialog';

  /** Ignores repeated presses on "call" so one press never becomes two calls. */
  const CALL_REQUEST_DEBOUNCE_MS = 3000;
  
  const tn: TranslateNew = TranslateNew.getInstance();
  
  interface IProps extends RouteComponentProps, WithStyles<typeof chatStyle> {
    userId: number;
    family: IFamilyMember[];
    chats:IChat[];
    suggestions:ISuggestion[];
    isOnline: boolean;
    isAuthenticated: boolean;
    requestChatMessages: (request:IRequestChatMessages) => void;
    sendChatReadReceipt: (receipt:IChatReceipt) => void;
    archiveChat:(chatId:number) => void;
    displayErrorMessage: (msg: string) => void;
    displaySuccessMessage: (msg: string) => void;
    saveChat:(chatId:number) => void;
    call:(call:ICall) => void;
    calls:ICall[];
  }
  
  interface IState {
    openMenu:boolean;
    openNewChat:boolean;
    showArchived:boolean;
    internalImages:Map<number, number[]>;
    hideFab:boolean;
    search:string;
    sentSearch:string;
    editMessage:string;
    replyTo:{chatId:number, messageId:number}|undefined; // the message the input's next send replies to
    showCallPermissionError:boolean;
    // isDownloading: number|undefined;
  }
  
  interface IRouteParams {
    chatId?: string;
  }

  interface IChatListResult {
    chat:IChat;
    targetMessageId?:number;
  }

  interface IVisualViewport {
    height:number;
    addEventListener:(type:"resize"|"scroll", listener:EventListenerOrEventListenerObject) => void;
    removeEventListener:(type:"resize"|"scroll", listener:EventListenerOrEventListenerObject) => void;
  }
  
  class ChatContainer extends React.Component<IProps, IState> {
    private menuRef:React.RefObject<HTMLButtonElement>=React.createRef();
    private paneRef:React.RefObject<HTMLDivElement>=React.createRef();
    private bodyOverflow:string = "";
    private lifecycleMounted:boolean = false;
    private visualViewport:IVisualViewport|undefined;
    private viewportHeight:number = 0;
    private viewportCompensationY:number = 0;
    private viewportSyncFrame:number|undefined;
    private viewportSyncUntil:number = 0;
    private lastCallRequestAt:number = 0;
    private callPermissionInFlight:boolean = false;

    constructor(props: IProps) {
      super(props);

      this.state = {
        openMenu:false,
        openNewChat:false,
        showArchived:false,
        internalImages: new Map<number, number[]>(),
        hideFab: false,
        search:"",
        sentSearch:"",
        editMessage:"",
        replyTo:undefined,
        showCallPermissionError:false,
        // isDownloading: undefined,
      };
    }
  
    // Update the chat room height directly using the ref
    public updateChatRoomHeight = ():boolean => {
      const pane = this.paneRef.current;
      if (!this.lifecycleMounted || !pane) {
        return false;
      }

      const visualViewport = this.visualViewport;
      const rawViewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
      if (!Number.isFinite(rawViewportHeight) || rawViewportHeight <= 0) {
        return false;
      }

      const viewportHeight = Math.max(1, Math.round(rawViewportHeight));

      // iOS 26 can visually pan a WKWebView without updating scrollY, and its
      // visualViewport.offsetTop may be stale or smaller than the real movement.
      // Measure the fixed pane itself and correct its actual screen position.
      // Because the previous compensation is included in paneTop, this also
      // removes a stale transform as soon as the keyboard is dismissed.
      const paneTop = pane.getBoundingClientRect().top;
      let viewportCompensationY = this.viewportCompensationY;
      if (Number.isFinite(paneTop) && Math.abs(paneTop) >= 0.5) {
        const maxCompensation = Math.max(window.innerHeight || 0, viewportHeight);
        viewportCompensationY = Math.max(
          0,
          Math.min(maxCompensation, Math.round(this.viewportCompensationY - paneTop))
        );
      }

      const viewportChanged = viewportHeight !== this.viewportHeight
        || viewportCompensationY !== this.viewportCompensationY;
      if (!viewportChanged) {
        return false;
      }

      this.viewportHeight = viewportHeight;
      this.viewportCompensationY = viewportCompensationY;

      pane.style.height = `${viewportHeight}px`;
      pane.style.minHeight = `${viewportHeight}px`;
      pane.style.maxHeight = `${viewportHeight}px`;
      pane.style.transform = `translate3d(0, ${viewportCompensationY}px, 0)`;
      return true;
    };

    public componentDidMount() {
      this.lifecycleMounted = true;
      this.visualViewport = this.getVisualViewport();
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      // Set initial height
      this.updateChatRoomHeight();
      this.startViewportSync(400);
      window.addEventListener("resize", this.handleViewportChange);
      document.addEventListener("focusin", this.handleFocusChange);
      document.addEventListener("focusout", this.handleFocusChange);
      // Re-measure when the app comes back from the background: the keyboard can
      // be dismissed while hidden without ever firing a viewport resize event.
      document.addEventListener("resume", this.handleAppResume, false);
      document.addEventListener("visibilitychange", this.handleVisibilityChange, false);
      window.addEventListener("pageshow", this.handleAppResume);
      if (this.visualViewport) {
        this.visualViewport.addEventListener("resize", this.handleViewportChange);
        this.visualViewport.addEventListener("scroll", this.handleViewportChange);
      }
    }

    public componentWillUnmount() {
      this.lifecycleMounted = false;
      this.viewportSyncUntil = 0;
      document.body.style.overflow = this.bodyOverflow;
      if (this.viewportSyncFrame !== undefined) {
        window.cancelAnimationFrame(this.viewportSyncFrame);
        this.viewportSyncFrame = undefined;
      }
      window.removeEventListener("resize", this.handleViewportChange);
      document.removeEventListener("focusin", this.handleFocusChange);
      document.removeEventListener("focusout", this.handleFocusChange);
      document.removeEventListener("resume", this.handleAppResume, false);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange, false);
      window.removeEventListener("pageshow", this.handleAppResume);
      if (this.visualViewport) {
        this.visualViewport.removeEventListener("resize", this.handleViewportChange);
        this.visualViewport.removeEventListener("scroll", this.handleViewportChange);
        this.visualViewport = undefined;
      }
    }

    public render() {
      const { classes, chats, family, userId, isAuthenticated, suggestions}  = this.props;
      const { search, sentSearch }  = this.state;

      let selectedChatId:number|undefined;
      if (this.props.match && this.props.match.params) {
        const { chatId }:IRouteParams = this.props.match.params as IRouteParams;
        if (chatId) {
          const chatIdParsed = parseInt(chatId,10);
          if (Number.isFinite(chatIdParsed)) {
            selectedChatId = chatIdParsed;
          }
        }
      }
      const selectedChat = selectedChatId !== undefined
        ? chats.find((chat) => chat.id === selectedChatId)
        : undefined;
      const selectedLoadedChatId = selectedChat ? selectedChatId : undefined;
      const { replyTo } = this.state;
      const replyToId = replyTo !== undefined && replyTo.chatId === selectedLoadedChatId ? replyTo.messageId : undefined;

      return (
        <div className={classes.container}>
            <CallPermissionDialog
              open={this.state.showCallPermissionError}
              onClose={() => this.setState({showCallPermissionError:false})}
            />
            <div className={classes.search}>
                <div className={classes.searchBox}>
                    <input 
                        className={classes.searchInput} 
                        placeholder={tn.lang.chatSearch}
                        value={this.state.search}
                        onChange={(evt) => this.setState({search:evt.target.value})}/>
                    <IconButton 
                        style={(search !== sentSearch && search.trim().length > 2)?{backgroundColor:secondaryColor}:{}}
                        disabled={(search === sentSearch || search.trim().length < 3)}
                        onClick={() => { if (this.state.search.trim().length > 2) {
                            this.setState({sentSearch:this.state.search});
                            this.props.requestChatMessages({chatId:0,numMsgsBack:1,startId:0,search:this.state.search})}}}>
                        <SearchIcon style={(search !== sentSearch && search.trim().length > 2)?{color:styleColors.white}:{}}/>
                    </IconButton>
                </div>
                <IconButton ref={this.menuRef} onClick={() => this.setState({openMenu:true})}
                    style={{marginTop: '2px',marginRight: '12px', marginLeft: '12px'}}>
                    <SettingsIcon style={{fontSize:'30px', fill:styleColors.blackAlpha45}}/>
                </IconButton>
            </div>
            <div className={classes.chatList}>
                {this.filter(this.state.search,chats).map((result) => this.generateChatListItem(result, selectedChatId))}
            </div>
            <div className={classes.FAB} onClick={() => this.setState({openNewChat:true})}
                style={this.state.hideFab?{scale:0, pointerEvents:'none'}:{}}>
                <AddIcon style={{fontSize:'42px'}}/>
            </div>
            <Dialog
                open={ this.state.openNewChat}
                onClose={() => this.setState({openNewChat:false})}
                classes={{paper:this.props.classes.bigDialog}}
            >
                <DialogTop>
                    <DialogTitle>{tn.lang.createNewChat}</DialogTitle>
                    <IconButton onClick={() => this.setState({openNewChat:false})}>
                        <CloseIcon />
                    </IconButton>
                </DialogTop>
                <EditChat close={() => this.setState({openNewChat:false})} open={this.state.openNewChat}/>
            </Dialog>
            <BottomBar 
                hidden={false}
                displaySubmenyCallback={(hide:boolean) => {this.setState({hideFab:hide})}}
            />
            <div className={classes.chatRoomViewport} style={{pointerEvents:selectedChatId !== undefined ? 'auto' : 'none'}} ref={this.paneRef}>
              <ChatPane className={classes.chatRoomDetails} chatId={selectedChatId}>
                {(isOpening, slidePane) => <>
                <ChatTopBar 
                    chatId={selectedChatId} 
                    chats={chats}
                    userId={userId}
                    family={family}
                    isOnline={this.props.isOnline}
                    call={(chatId:number) => this.call(chatId)}
                    archiveChat={this.props.archiveChat}
                    back={() => {this.selectChatId(undefined)}}
                    downloadChatHistory={() => {this.downloadChatHistory(selectedChatId)}}
                    // isDownloading={this.state.isDownloading}
                    />
                <ChatRoom
                      key={selectedChatId === undefined ? 'closed' : selectedChatId}
                      isOpening={isOpening}
                      userId={userId}
                      family={family}
                      chatId={selectedChatId}
                      showAllMessages={(this.state.sentSearch.length > 0 ) ? true : false}
                      chats={chats}
                      calls={this.props.calls}
                      suggestions={suggestions}
                      requestChatMessages={this.props.requestChatMessages}
                      sendChatReadReceipt={this.props.sendChatReadReceipt}
                      isAuthenticated={isAuthenticated}
                      targetMessageId={this.getTargetMessageId()}
                      setEditMessage={(msg: string) => this.setState({editMessage:msg})}                 
                      setReplyTo={(messageId: number) => {
                        if (selectedChatId !== undefined) {
                          this.setState({replyTo:{chatId:selectedChatId, messageId}});
                        }
                      }}
                />
                <ChatBottomBar chatId={selectedLoadedChatId} hideFAB={this.state.hideFab} savedMessage={this.state.editMessage}
                    clearSavedMessage={() => this.setState({editMessage:""})}
                    replyToId={replyToId} clearReplyTo={() => this.setState({replyTo:undefined})}/>
                <div style={{height: '100px', width: '100vw', position: 'absolute', bottom: '-100px', 
                  backgroundColor: 'var(--color-greyLight2)', zIndex: 1}} />
                <BackSwipe chatId={selectedChatId} back={() => {this.selectChatId(undefined)}} pane={slidePane}/>
                </>}
              </ChatPane>
            </div>
            <Menu
                anchorEl={this.menuRef.current}
                open={this.state.openMenu}
                anchorReference="anchorEl"
                onClose={() => this.setState({openMenu:false})}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                classes={{
                paper: classes.menuPaper,
                list: classes.menuList,
                }}
            >

                {/* show archive */}
                <MenuItem
                    className={classes.menuListItem}
                    onClick={() => {this.setState({showArchived:!this.state.showArchived})}}>  
                    <ListItemIcon>
                        <Checkbox color="primary" id="showweek" checked={this.state.showArchived} readOnly={true} edge="start"/>
                    </ListItemIcon>        
                    
                    <div className={classes.menuItemTitle}>
                        {tn.lang.chatShowArchive}
                    </div>
                </MenuItem>
            </Menu>
        </div>
      );
    }

    private getVisualViewport = ():IVisualViewport|undefined => {
      return (window as Window & {visualViewport?:IVisualViewport}).visualViewport;
    }

    private handleViewportChange = () => {
      this.startViewportSync();
    }

    private handleAppResume = () => {
      // Coming back from the background can leave the pane stuck at the height it
      // had while the keyboard was open (input floating mid-screen), because the
      // dismiss happens while hidden and no resize event reaches us. Re-measure
      // and keep polling for a bit so the height is corrected once the webview
      // finishes settling on resume.
      this.startViewportSync(1500);
    }

    private handleVisibilityChange = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        this.handleAppResume();
      }
    }

    private handleFocusChange = (event:Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (this.shouldTrackKeyboard(target)) {
        this.startViewportSync(event.type === "focusin" ? 900 : 700);
      }
    }

    private shouldTrackKeyboard = (target:HTMLElement):boolean => {
      return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
    }

    private startViewportSync = (duration:number = 600) => {
      if (!this.lifecycleMounted) {
        return;
      }

      this.viewportSyncUntil = Math.max(this.viewportSyncUntil, Date.now() + duration);
      if (this.viewportSyncFrame === undefined) {
        this.viewportSyncFrame = window.requestAnimationFrame(this.syncViewportFrame);
      }
    }

    private syncViewportFrame = () => {
      this.viewportSyncFrame = undefined;
      if (!this.lifecycleMounted) {
        return;
      }

      try {
        const viewportChanged = this.updateChatRoomHeight();
        if (viewportChanged || Date.now() < this.viewportSyncUntil) {
          this.viewportSyncFrame = window.requestAnimationFrame(this.syncViewportFrame);
        }
      } catch (error) {
        this.viewportSyncUntil = 0;
        this.reportViewportError(error);
      }
    }

    private reportViewportError(error:unknown) {
      try {
        (window as any).reportHandledError?.("Chat viewport lifecycle", error, {
          stage:"animation-frame",
          chatId:(this.props.match.params as IRouteParams | undefined)?.chatId,
          viewportHeight:this.viewportHeight,
          viewportCompensationY:this.viewportCompensationY,
        });
      } catch (reportError) {
        console.warn("Failed to report chat viewport error:", reportError);
      }
    }
  
    private getTargetMessageId():number|undefined {
        return getChatTargetMessageId(this.props.location.search);
    }
  
    private generateChatListItem = (result:IChatListResult, selectedChat:number|undefined) => {
        const chat = result.chat;
        const isArchived = chat.hidden ? chat.hidden.includes(this.props.userId) : false
        let hide = this.state.showArchived 
            ? !isArchived
            : isArchived
        const messages:IChatMessage[] = Array.isArray(chat.messages) ? chat.messages : [];
        const participants = chat.participants ? chat.participants : {};
        const lastMessage:IChatMessage|false = messages.length > 0 && messages[messages.length - 1];
        let sender:string ="";
        let message:string = "";
        let time:string = "";
        let badgeCount:number = 0;
        if (lastMessage && this.props.family) {
            if (lastMessage.senderId === 0 && chat.chatType && chat.chatType === ChatType.SYSTEM_CHAT) {
                sender = tn.lang.support;
            } else {
                sender = tn.familyMemberName(lastMessage.senderId, this.props.family);
            }
            
            const msgText = typeof lastMessage.msg === "string" ? lastMessage.msg : "";
            message = msgText.length > 0
                ? msgText
                : lastMessage.fileDesc
                    ? "🖼️"
                    : "";
            const timeAgo = Date.now() - lastMessage.sendingTime;
            if (timeAgo < (1000*60*60*24)) {
                time = tn.formatTime(new Date(lastMessage.sendingTime));
            } else if (timeAgo < (1000*60*60*24*7)) {
                time = tn.lang.getWeekdayName((new Date(lastMessage.sendingTime)).getDay())
            } else {
                time = tn.formatDateMonthShort(new Date(lastMessage.sendingTime))
            }
            const me = participants[""+this.props.userId];
            if (me) {
                badgeCount = (Math.max(messages[messages.length - 1].id - me.lastRead, 0));
            }
        }

        if (badgeCount > 0) {
            hide = false;
        }
        return <ChatListItem 
            hide={hide}
            key={""+chat.id}
            chatId={chat.id}
            icon={chat.icon}
            lastMessage={message}
            lastMessageSender={sender}
            hidePreview={!!lastMessage && chatListPreviewsHidden(this.props.family)}
            lastTime={time}
            onClick={() => {this.selectChat(chat, result.targetMessageId)}}
            selectedChatId={selectedChat}
            title={typeof chat.title === "string" ? chat.title : ""}
            archived={isArchived}
            badgeCount={badgeCount}
        />
    }

    private filter(text:string, chats:IChat[]):IChatListResult[] {
        
        if (text.trim().length > 2 ) {
            const newFiltered: IChatListResult[] = [];
            for (const chat of chats) {
                const messages:IChatMessage[] = Array.isArray(chat.messages) ? chat.messages : [];
                const title = typeof chat.title === "string" ? chat.title : "";
                const foundMsg = messages.find((msg) => {
                    const msgText = typeof msg.msg === "string" ? msg.msg : "";
                    return msgText.toLowerCase().includes(text.toLowerCase());
                })
                if (foundMsg ) {
                    const chatCopy:IChat = {
                        chatType:chat.chatType,
                        icon:chat.icon,
                        id:chat.id,
                        participants:chat.participants ? chat.participants : {},
                        referenceObject:chat.referenceObject,
                        title,
                        hidden:chat.hidden,
                        messages:[foundMsg],
                    };
                    newFiltered.push({chat:chatCopy, targetMessageId:foundMsg.id});
                } else if (title.toLowerCase().includes(text.toLowerCase())) {
                    newFiltered.push({chat});
                }
            }
            return newFiltered;
        }
        else return chats.map((chat) => ({chat}));
      }

    private selectChatId(chatId:number|undefined, targetMessageId?:number) {
        const id = chatId !== undefined ? chatId : ":chatId"
        this.props.history.push(buildChatPath(id, targetMessageId));
    }

    private selectChat(chat:IChat, targetMessageId?:number) {
        this.selectChatId(chat.id, targetMessageId);
    }

    private downloadChatHistory = (chatId: number | undefined) => {
      if (!chatId) return;

      this.props.saveChat(chatId);
      this.props.displaySuccessMessage(tn.lang.chatHistorySaved);

      // this.loadAllHistory(chatId);
    };

    private call = async (chatId:number) => {
      if (this.props.isOnline) {
        // Two presses in quick succession used to create rival calls that the apps then
        // failed to agree on.
        if (this.callPermissionInFlight || Date.now() - this.lastCallRequestAt < CALL_REQUEST_DEBOUNCE_MS) {
          return;
        }

        const participantIds:number[] = [];

        for (const chat of this.props.chats) {
          if (chat.id === chatId) {
            if (chat.participants) {
              
              for (const participantIdString of Object.keys(chat.participants || {})) {
                const participantId:number = Number(participantIdString);
                console.log(participantId);
                if (Number.isFinite(participantId) && this.shouldIncludeParticipantInCall(participantId)) {
                  participantIds.push(participantId);
                }
              }
              
            }
          }
        } 
        if (participantIds.length < 2) {
          this.props.displayErrorMessage(tn.lang.error);   
          return;
        }
        const call:ICall = {
          chatId,
          initiator: this.props.userId,
          start: Date.now(),
          callStatus: 'CREATE',
          participants: participantIds.map((p) => {const pa:ICallParticipant = {participant:p, responseStatus:"AWAITING"}; return pa; }),
        }

        // Camera and microphone are settled before the call exists. Granting them opens a
        // system dialog that backgrounds the app on Android, and doing that after the call
        // is placed leaves the other side ringing a call whose media session cannot
        // survive the trip to the background.
        if (!await this.acquireCallPermission()) {
          return;
        }

        if (!this.props.isOnline || Date.now() - this.lastCallRequestAt < CALL_REQUEST_DEBOUNCE_MS) {
          return;
        }

        // Placed now, so the backend sees the time the call actually goes out rather than
        // whenever the button was pressed.
        call.start = Date.now();
        this.lastCallRequestAt = Date.now();
        this.props.call(call); 
      } else {
        // this.props.displayErrorMessage(tn.lang.noConnection);
      }
    }

    /**
     * Asks for camera and microphone. Refusing explains the way out rather than just
     * saying no: once Android has been told "don't allow" twice it stops asking, and the
     * app's settings page is the only place left to turn the permission back on.
     */
    private acquireCallPermission = async (): Promise<boolean> => {
      this.callPermissionInFlight = true;
      try {
        const {granted} = await ensureCallPermission();
        if (!granted) {
          this.setState({showCallPermissionError:true});
        }
        return granted;
      } catch (error) {
        console.log("Call permission request failed:", error);
        this.setState({showCallPermissionError:true});
        return false;
      } finally {
        this.callPermissionInFlight = false;
      }
    }

    private shouldIncludeParticipantInCall(participantId:number): boolean {
      const member = this.props.family.find((familyMember) => familyMember.id === participantId);
      return !!member && (
        member.role === FamilyMemberType.CHILD
        || member.role === FamilyMemberType.PARENT
        || member.role === FamilyMemberType.VIEWER
      );
    }
  
    
  }
  
  const EMPTY_ARRAY: any[] = [];

  // mapStateToProps runs for every dispatched action, and the filter+sort here
  // used to create a brand-new chats array each time, re-rendering the whole
  // chat tree on actions that did not touch chat state. The derivation is
  // memoized on the chats slice reference (the chat reducer replaces it
  // immutably on every real change), so the cached array must never be
  // mutated by consumers.
  let lastRawChats: any = null;
  let lastSortedChats: IChat[] = EMPTY_ARRAY;

  function deriveSortedChats(rawChats: any): IChat[] {
    if (rawChats === lastRawChats) {
      return lastSortedChats;
    }

    // Rehydrated persisted state bypasses the reducer normalizers, so entries and
    // their messages can have unexpected shapes until the first snapshot arrives.
    const sorted: IChat[] = Array.isArray(rawChats)
      ? rawChats.filter((chat: any) => chat && typeof chat === "object")
      : [];

    sorted.sort((a,b) => {
        const aMessages = Array.isArray(a.messages) ? a.messages : [];
        const bMessages = Array.isArray(b.messages) ? b.messages : [];
        if (aMessages.length === 0 || bMessages.length === 0) return (b.id - a.id);
        return (bMessages[bMessages.length - 1].sendingTime - aMessages[aMessages.length - 1].sendingTime );
    });

    lastRawChats = rawChats;
    lastSortedChats = sorted;
    return sorted;
  }

  function mapStateToProps(state: any) {
    const userId:number = state?.user?.session?.data ? state.user.session.data.id : 0;
    const family:IFamilyMember[] = state?.family?.family ? state.family.family : EMPTY_ARRAY;

    return {
      chats: deriveSortedChats(state?.chat?.chats),
      family,
      userId,
      uploadingFiles: state?.file?.uploadSlots ? state.file.uploadSlots : EMPTY_ARRAY,
      uploadedFiles: state?.file?.uploadedFiles ? state.file.uploadedFiles : EMPTY_ARRAY,
      suggestions:state?.suggestion?.suggestions?state.suggestion.suggestions:EMPTY_ARRAY,
      isOnline:!!state?.connection?.loadCompleted,
      isAuthenticated:!!state?.connection?.authenticated || !!state?.connection?.loadCompleted,
      calls:state?.calls?.calls?state.calls.calls:EMPTY_ARRAY,
    };
  }
  
  const mapDispatchToProps = {
    createChat: ChatActions.createChat,
    requestChatMessages: ChatActions.requestChatMessages,
    sendChatReadReceipt: ChatActions.sendChatReadReceipt,
    saveChat: ChatActions.saveChat,
    archiveChat: ChatActions.archiveChat,
    displayErrorMessage,
    displaySuccessMessage,
    call:CallActions.newCall,
  };
  
  export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(chatStyle)(ChatContainer)));
  
  
  



// WEBPACK FOOTER //
// ./src/containers/chat/chat2.container.tsx